// src/context/site/SiteManagerModal.jsx
//
// The "Sito aziendale" modal (KB-web-after-Button-Gestisci.png): the connected
// site with [Modifica], the status chip with [Ricarica contenuti] and
// [Reindicizza], the Needs-review actions, a searchable paginated URL table
// with Mantieni / Escludi checkboxes, [Aggiungi URL], and a footer with the
// pending-change counter, [Annulla] and [Salva modifiche].
//
// EDIT LOCALLY, SAVE ONCE. No checkbox PATCHes anything. Every change is a
// pending desired state keyed by URL (site/urlDelta.js) — so it survives
// paging and search — and [Salva modifiche] folds all of them into ONE PATCH
// under ONE If-Match. After a 200 the page is RE-FETCHED (pinning relocates
// rows). If the etag changes under pending edits (another tab, the CLI), the
// edits are dropped with a notice: they targeted a list that no longer exists.
//
// THE LOCK IS UX ONLY. While the chip reads Discovering/Indexing everything
// that writes is disabled — and a 409 KB_JOB_ACTIVE is still handled on every
// write, because a job can start from another tab, another admin, or the CLI.
//
// Props: `status` + `refreshStatus` come from the card's ONE poller; this
// component never polls and never sets the chip locally — every action that
// can change the state ends with refreshStatus().
//
// Refusal wording: SITE_APEX_CHANGED → a destructive confirm built from
// err.details, then ONE re-post with confirm_replace (never automatic);
// 402 / TENANT_RETIRED / LOOKUP_FAILED → RunRefusalNotice via refusalFromError;
// CONTEXT_URL_DELTA_REFUSED and CONTEXT_EXCLUDE_FILE_INVALID → our headline +
// the server message verbatim (it names the offending URL / file); the KB_*
// codes → errors.json copy through parseApiError.
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import useAdvancedView from '../../hooks/useAdvancedView.js';
import AdvancedToggle from '../../components/ui/AdvancedToggle.jsx';
import { Search, Lock, X, RotateCw, RefreshCw, HelpCircle } from 'lucide-react';
import AdminModal from '../../admin/components/AdminModal.jsx';
import AdminPagination from '../../admin/components/AdminPagination.jsx';
import ConfirmDialog from '../../admin/components/ConfirmDialog.jsx';
import RunRefusalNotice from '../../components/credits/RunRefusalNotice.jsx';
import { refusalFromError } from '../../utils/runRefusal.js';
import {
  getWebsiteUrls, patchWebsiteUrls, putWebsiteSite, reindexWebsite, reloadWebsite,
  promoteKbShadow, discardKbShadow,
} from '../contextApi.js';
import {
  computeDelta, isEmptyDelta, countPending, effectiveRow, withFlag, readApplied, normaliseUrl,
  APPLIED_KEYS,
} from './urlDelta.js';
import { isApexChange, registrableDomain } from './apexChange.js';
import { siteErrorCopy } from './siteErrorCopy.js';
import { kbFailureHint, showKbFailure } from './kbFailureCopy.js';
import { isKbBusy } from './useKbStatus.js';
import KbStatusChip from './KbStatusChip.jsx';
import KbJobBanner from './KbJobBanner.jsx';
import { COLORS, FONT_SANS, RADII, SEMANTIC } from '../../constants/theme.js';

const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 300;

export default function SiteManagerModal({
  tenantId, site, status, refreshStatus, onSiteChanged, onClose, onJobStarted, userKey = '',
  addressFixed = false, onSiteSaved,
}) {
  const { t } = useTranslation('context');
  // v201: «Aggiorna contenuti esistenti» and the URL list (search, table, pagination, "Aggiungi URL") are an
  // "Avanzate" part — one per-browser, per-user preference (useAdvancedView + AdvancedToggle, the app's one
  // mechanism). Hiding never drops an edit: the pending count shows on the toggle, and the footer's Salva stays.
  const [advanced, setAdvanced] = useAdvancedView('kb-site', userKey);

  // ── table ────────────────────────────────────────────────────────────────
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  // ⛔ No `draft_pending`: since BE 7f7f18e it is a support signal only — never shown to a workspace admin.
  const [meta, setMeta] = useState({ exclusions_error: null, exclusions_edit_blocked: null });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [offset, setOffset] = useState(0);

  // ── pending edits, keyed by URL (survive paging / search) ────────────────
  const [pending, setPending] = useState(() => new Map());
  const [added, setAdded] = useState([]);
  const [removed, setRemoved] = useState(() => new Set());
  const baselineRef = useRef(new Map()); // url → server row, under the current etag
  const etagRef = useRef(null);
  const pendingCountRef = useRef(0);

  // ── feedback ─────────────────────────────────────────────────────────────
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  // A WARNING-toned notice — today only «Annulla la nuova lettura» restoring another domain's list.
  const [warnNotice, setWarnNotice] = useState('');
  const [refusal, setRefusal] = useState(null);
  const [saving, setSaving] = useState(false);
  const [triggering, setTriggering] = useState(false);
  const [reviewing, setReviewing] = useState(false);

  // ── site edit ────────────────────────────────────────────────────────────
  const [editingSite, setEditingSite] = useState(false);
  const [siteDraft, setSiteDraft] = useState('');
  const [siteSaving, setSiteSaving] = useState(false);
  const [siteError, setSiteError] = useState('');
  const [apexWarn, setApexWarn] = useState(null);   // { from, to, site }
  const [confirmEditSite, setConfirmEditSite] = useState(false); // [Modifica] asks before the field opens

  // ── dialogs / add form ───────────────────────────────────────────────────
  const [confirmTrigger, setConfirmTrigger] = useState(null); // 'reload' | 'reindex' | null
  const [helpOpen, setHelpOpen] = useState(null);               // v201: 'base' | 'compare' | null — which explainer
  const [apexReplace, setApexReplace] = useState(null);       // err.details of SITE_APEX_CHANGED
  const [addOpen, setAddOpen] = useState(false);
  const [addUrl, setAddUrl] = useState('');
  const [addError, setAddError] = useState('');

  const connectedSite = site && site.site && !site.is_sentinel ? site.site : null;
  // The site is view-only until [Modifica] is confirmed, and a save is only
  // offered when the (normalised) draft differs from what is connected: an
  // unchanged site never issues a PUT.
  const siteUnchanged = normaliseUrl(siteDraft) === normaliseUrl(connectedSite);
  const state = status?.state;
  const busy = isKbBusy(state);
  // When the job ends under an open modal, re-read the page: a reload merges
  // new pages into the list, and the user should not have to close and reopen.
  const prevBusyRef = useRef(busy);
  const locked = busy || saving;
  const pendingCount = countPending({ baseline: baselineRef.current, pending, added, removed });
  pendingCountRef.current = pendingCount;

  const resetPending = useCallback(() => {
    setPending(new Map());
    setAdded([]);
    setRemoved(new Set());
    // The ref is otherwise refreshed on render, and a caller that resets and
    // then fetches (handleSave) would run fetchPage BEFORE React commits the
    // reset — its stale-etag guard would then read the pre-reset count.
    pendingCountRef.current = 0;
  }, []);

  // Resolves with the rows it read (null when the read failed).
  const fetchPage = useCallback(async ({ q: query, offset: off }) => {
    let fetched = null;
    setLoading(true);
    setError('');
    try {
      const data = await getWebsiteUrls({
        tenant: tenantId, q: query || undefined, limit: PAGE_SIZE, offset: off,
      });
      if (etagRef.current && data.etag !== etagRef.current && pendingCountRef.current > 0) {
        // Another writer (a tab, the CLI) changed the file under the user's
        // edits: the delta targets a list that no longer exists.
        resetPending();
        baselineRef.current = new Map();
        setNotice(t('site.modal.etagNotice'));
      }
      etagRef.current = data.etag;
      (data.urls || []).forEach((r) => baselineRef.current.set(r.url, r));
      setRows(data.urls || []);
      setTotal(data.total || 0);
      fetched = data.urls || [];
      setMeta({
        exclusions_error: data.exclusions_error || null,
        exclusions_edit_blocked: data.exclusions_edit_blocked || null,
      });
    } catch (e) {
      setError(siteErrorCopy(t, e));   // v201: never the server's sentence
    } finally {
      setLoading(false);
    }
    return fetched;
  }, [tenantId, t, resetPending]);

  useEffect(() => {
    const id = setTimeout(() => {
      setDebouncedQ(q.trim());
      setOffset(0);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [q]);

  useEffect(() => {
    fetchPage({ q: debouncedQ, offset });
  }, [fetchPage, debouncedQ, offset]);

  useEffect(() => {
    if (prevBusyRef.current && !busy) fetchPage({ q: debouncedQ, offset });
    prevBusyRef.current = busy;
  }, [busy, fetchPage, debouncedQ, offset]);

  // ── edits ────────────────────────────────────────────────────────────────

  // v201: "Mantieni" is not offered on this surface for now. The backend lets a PINNED page win over its exclusion
  // (it would still be read), so excluding a page that is still pinned in the data also unpins it.
  function setFlag(row, key, value) {
    setPending((prev) => {
      const next = withFlag(prev, row, key, value);
      return key === 'excluded' && value && row.pinned ? withFlag(next, row, 'pinned', false) : next;
    });
  }

  function toggleRemoved(url) {
    setRemoved((prev) => {
      const next = new Set(prev);
      if (next.has(url)) next.delete(url);
      else next.add(url);
      return next;
    });
  }

  function submitAdd() {
    const normalised = normaliseUrl(addUrl);
    if (!normalised) {
      setAddError(t('site.modal.addInvalid'));
      return;
    }
    if (baselineRef.current.has(normalised) || added.some((a) => a.url === normalised)) {
      setAddError(t('site.modal.addDuplicate'));
      return;
    }
    setAdded((prev) => [...prev, { url: normalised, pin: false }]);   // v201: no "Mantieni" for an added URL
    setAddUrl('');
    setAddError('');
    setAddOpen(false);
  }

  function summarise(applied) {
    const counts = readApplied(applied);
    // v201: pinned / unpinned are not reported — the user never sees that flag here.
    const parts = APPLIED_KEYS
      .filter((k) => counts[k] > 0 && k !== 'pinned' && k !== 'unpinned')
      .map((k) => t(`site.modal.summary.${k}`, { count: counts[k] }));
    return parts.length ? parts.join(', ') : t('site.modal.summary.none');
  }

  async function handleSave() {
    const delta = computeDelta({ baseline: baselineRef.current, pending, added, removed });
    if (isEmptyDelta(delta)) {
      setNotice(t('site.modal.savedNothing'));
      return;
    }
    setSaving(true);
    setError('');
    setNotice('');
    setWarnNotice('');
    setRefusal(null);
    try {
      const res = await patchWebsiteUrls({ tenant: tenantId, etag: etagRef.current, delta });
      etagRef.current = res.etag;
      resetPending();
      baselineRef.current = new Map();
      setNotice(`${t('site.modal.saved', { summary: summarise(res.applied) })} ${t('site.modal.savedNote')}`);
      await fetchPage({ q: debouncedQ, offset }); // pinning relocates rows
      refreshStatus();
    } catch (err) {
      if (err.code === 'CONTEXT_ETAG_MISMATCH') {
        resetPending();
        baselineRef.current = new Map();
        etagRef.current = null;
        setNotice(t('site.modal.etagNotice'));
        await fetchPage({ q: debouncedQ, offset });
      } else if (err.code === 'CONTEXT_URL_DELTA_REFUSED') {
        setError(t('site.modal.deltaRefused', { message: err.message }));
      } else if (err.code === 'CONTEXT_EXCLUDE_FILE_INVALID') {
        setError(t('site.modal.excludeFileInvalid', { message: err.message }));
      } else {
        // KB_JOB_ACTIVE / KB_JOB_LOOKUP_FAILED / CONTEXT_URL_SURGERY_FAILED have FE copy (errors.json); v201: any
        // other code is worded by siteErrorCopy, never the server's sentence. Pending edits are KEPT.
        setError(siteErrorCopy(t, err));
        if (err.code === 'KB_JOB_ACTIVE') refreshStatus();
      }
    } finally {
      setSaving(false);
    }
  }

  // ── triggers ─────────────────────────────────────────────────────────────

  async function runTrigger(kind, { confirmReplace = false } = {}) {
    setTriggering(true);
    setError('');
    setNotice('');
    setWarnNotice('');
    setRefusal(null);
    try {
      if (kind === 'reload') await reloadWebsite({ tenant: tenantId, confirmReplace });
      else await reindexWebsite({ tenant: tenantId });
      setNotice(t('site.modal.jobStarted'));
      onJobStarted?.(kind); // the card's poller shows the job at once, before its next tick
    } catch (err) {
      if (err.code === 'SITE_APEX_CHANGED') {
        setApexReplace(err.details || {});
      } else {
        const verdict = refusalFromError(err);
        if (verdict) setRefusal(verdict);
        else setError(siteErrorCopy(t, err)); // KB_JOB_ACTIVE / KB_JOB_CAPACITY have errors.json copy
      }
    } finally {
      setTriggering(false);
      refreshStatus();
    }
  }

  // ── needs review ─────────────────────────────────────────────────────────

  async function handlePromote() {
    setReviewing(true);
    setError('');
    setNotice('');
    setWarnNotice('');
    try {
      await promoteKbShadow({ tenant: tenantId });
      setNotice(t('site.modal.review.accepted'));   // v201: plain words, no chunk counts
    } catch (err) {
      setError(siteErrorCopy(t, err));
    } finally {
      setReviewing(false);
      refreshStatus();
    }
  }

  async function handleDiscard() {
    setReviewing(true);
    setError('');
    setNotice('');
    setWarnNotice('');
    try {
      const r = await discardKbShadow({ tenant: tenantId });
      if (r.list_restored === true) {
        // BE 7f7f18e: the URL list the reload replaced is back — re-read it. After a DOMAIN change the site
        // address still names the new domain while the list is the old site's: every row then reads
        // `off_apex` against the connected site, which is how we tell (the response carries no flag).
        const restored = await fetchPage({ q: debouncedQ, offset });
        const otherDomain = Array.isArray(restored) && restored.length > 0 && restored.every(u => u.off_apex === true);
        if (otherDomain) setWarnNotice(t('site.modal.review.restoredOtherSite'));
        else setNotice(t('site.modal.review.restored'));
      } else {
        // `discarded: false` is a SUCCESS (nothing left to drop), never an error.
        setNotice(r.discarded ? t('site.modal.review.discarded') : t('site.modal.review.alreadyGone'));
      }
    } catch (err) {
      // ⛔ The chip is NOT cleared locally: on KB_SHADOW_DISCARD_FAILED the
      // shadow survived and the next poll will still say so.
      setError(siteErrorCopy(t, err));
    } finally {
      setReviewing(false);
      refreshStatus();
    }
  }

  // ── site edit ────────────────────────────────────────────────────────────

  function startEditSite() {
    setSiteDraft(connectedSite || '');
    setSiteError('');
    setEditingSite(true);
  }

  async function doSaveSite(nextSite) {
    setSiteSaving(true);
    setSiteError('');
    setError('');
    setNotice('');
    setWarnNotice('');
    try {
      await putWebsiteSite({ tenant: tenantId, site: nextSite, etag: site?.etag || undefined });
      setEditingSite(false);
      setNotice(t('site.modal.siteSaved'));
      // The card hides an address failure from here until the next job (see kbFailureCopy.js).
      if (onSiteSaved) onSiteSaved();
      await onSiteChanged();
      await fetchPage({ q: debouncedQ, offset });
    } catch (err) {
      if (err.code === 'CONTEXT_ETAG_MISMATCH') {
        setSiteError(t('site.modal.siteEtag'));
        await onSiteChanged();
      } else if (err.code === 'RESOURCE_NOT_FOUND') {
        setSiteError(t('site.noBrandFile'));
      } else if (err.status === 503) {
        // Contract: the provenance ledger is acquired BEFORE the write, so a 503
        // means nothing was written — and a stale If-Match surfaces as this 503
        // rather than a 412, so re-read the site before the user retries.
        setSiteError(t('site.modal.siteUnavailable'));
        await onSiteChanged();
      } else {
        setSiteError(siteErrorCopy(t, err));
        if (err.code === 'KB_JOB_ACTIVE') refreshStatus();
      }
    } finally {
      setSiteSaving(false);
    }
  }

  function requestSaveSite() {
    const normalised = normaliseUrl(siteDraft);
    if (!normalised) {
      setSiteError(t('errors:context.CONTEXT_INVALID_SITE_URL'));
      return;
    }
    if (normalised === normaliseUrl(connectedSite)) {
      setEditingSite(false); // nothing changed: close the field, no request
      return;
    }
    if (isApexChange(connectedSite, normalised)) {
      setApexWarn({ from: registrableDomain(connectedSite), to: registrableDomain(normalised), site: normalised });
      return;
    }
    doSaveSite(normalised);
  }

  // ── close ────────────────────────────────────────────────────────────────

  function requestClose() {
    if (pendingCount > 0 && !window.confirm(t('site.modal.discardPending'))) return;
    onClose();
  }

  // ── render ───────────────────────────────────────────────────────────────

  const showReview = state === 'Needs review';
  // An empty shadow cannot be promoted (409 KB_SHADOW_EMPTY), so [Accetta] is hidden.
  const shadowEmpty = status?.shadow_pending === 0;
  // exclusions_edit_blocked disables the Escludi column ONLY; Mantieni stays usable.
  const excludeBlocked = !!meta.exclusions_edit_blocked;
  const pageEmpty = !loading && rows.length === 0 && added.length === 0;

  return (
    <AdminModal open onClose={requestClose} title={t('site.modal.title')} width={820}>

      <div style={box}>
        <div style={label}>{t('site.modal.connectedSite')}</div>
        <div style={rowFlex}>
          {editingSite ? (
            <>
              <input
                type="url"
                value={siteDraft}
                onChange={(e) => setSiteDraft(e.target.value)}
                placeholder={t('site.modal.sitePlaceholder')}
                aria-label={t('site.modal.connectedSite')}
                disabled={siteSaving}
                style={input}
              />
              <button type="button" onClick={requestSaveSite} disabled={siteSaving || siteUnchanged} style={primaryBtn(siteSaving || siteUnchanged)}>
                {t('site.modal.saveSite')}
              </button>
              <button type="button" onClick={() => setEditingSite(false)} disabled={siteSaving} style={ghostBtn(siteSaving)}>
                {t('site.modal.cancelEdit')}
              </button>
            </>
          ) : (
            <>
              <div style={readonlyField} data-testid="modal-connected-site">
                {connectedSite || <span style={{ color: COLORS.textTertiary }}>{t('site.notConnected')}</span>}
              </div>
              <button type="button" onClick={() => setConfirmEditSite(true)} disabled={locked} style={primaryBtn(locked)}>
                {t('site.modal.edit')}
              </button>
            </>
          )}
        </div>
        {siteError && <p role="alert" style={hint(SEMANTIC.errore.fg)}>{siteError}</p>}

        <div style={{ ...rowFlex, marginTop: '14px' }}>
          <div>
            <div style={label}>{t('site.status.label')}</div>
            <KbStatusChip state={state} startedAt={status?.started_at} />
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            {/* Each trigger carries a native tooltip (`title`) saying WHEN to use it, the confirm that follows
                repeats it, and the ? opens the full explainer. `title` leaves the accessible NAME on the label, so
                role queries hold. v201: the base trigger is the complete one, «Rileggi tutto il Sito» (reload: map
                the site, then re-read every page); «Aggiorna contenuti esistenti» (reindex, no map) is the advanced
                shortcut, in the Avanzate part below. */}
            <button type="button" onClick={() => setConfirmTrigger('reload')} disabled={locked || triggering} title={t('site.modal.reloadHint')} style={ghostBtn(locked || triggering)}>
              <RotateCw size={14} /> {t('site.modal.reload')}
            </button>
            {/* v201: the base "?" explains THIS operation only (the other one is not on screen here). */}
            <button type="button" onClick={() => setHelpOpen('base')} aria-label={t('site.modal.helpBase.open')} title={t('site.modal.helpBase.open')} style={iconBtn}>
              <HelpCircle size={15} />
            </button>
          </div>
        </div>
        <KbJobBanner status={status} />
        {state === 'Failed' && showKbFailure(status?.failure_code, addressFixed) && (
          <p style={hint(SEMANTIC.errore.fg)}>{kbFailureHint(t, status?.failure_code)}</p>
        )}

        {showReview && (
          <div style={reviewBox}>
            <strong>{t('site.modal.review.title')}</strong>
            <p style={{ margin: '6px 0 10px' }}>
              {shadowEmpty
                ? t('site.modal.review.bodyEmpty')
                : t('site.modal.review.body')}
            </p>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {!shadowEmpty && (
                <button type="button" onClick={handlePromote} disabled={reviewing || busy} style={primaryBtn(reviewing || busy)}>
                  {t('site.modal.review.accept')}
                </button>
              )}
              <button type="button" onClick={handleDiscard} disabled={reviewing || busy} style={ghostBtn(reviewing || busy)}>
                {t('site.modal.review.discard')}
              </button>
            </div>
          </div>
        )}
      </div>

      {refusal && (
        <div style={{ marginTop: '12px' }}>
          <RunRefusalNotice refusal={refusal} onDismiss={() => setRefusal(null)} />
        </div>
      )}
      {error && <div role="alert" style={alertBox(SEMANTIC.errore.fg)}>{error}</div>}
      {notice && <div style={alertBox(SEMANTIC.verde.fg)}>{notice}</div>}
      {warnNotice && <div role="alert" style={alertBox(SEMANTIC.attenzione.fg)}>{warnNotice}</div>}
      {/* exclusions_error: a STANDING alert — the exclusion rules are NOT applied. */}
      {meta.exclusions_error && (
        <div role="alert" style={alertBox(SEMANTIC.errore.fg)}>
          <strong>{t('site.modal.banners.exclusionsError')}</strong>
          <div style={{ marginTop: '4px' }}>{meta.exclusions_error}</div>
        </div>
      )}

      <div style={{ marginTop: '16px' }}>
        <AdvancedToggle
          on={advanced} onChange={setAdvanced} label={t('site.modal.advanced')}
          hiddenCount={pendingCount} hiddenLabel={t('site.modal.advancedPending', { count: pendingCount })}
        />
      </div>

      {advanced && (
        <>
          <div style={{ marginTop: '12px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <button type="button" onClick={() => setConfirmTrigger('reindex')} disabled={locked || triggering} title={t('site.modal.reindexHint')} style={ghostBtn(locked || triggering)}>
              <RefreshCw size={14} /> {t('site.modal.reindex')}
            </button>
            {/* v201: here both operations are on screen, so this "?" compares them. */}
            <button type="button" onClick={() => setHelpOpen('compare')} aria-label={t('site.modal.help.open')} title={t('site.modal.help.open')} style={iconBtn}>
              <HelpCircle size={15} />
            </button>
          </div>
          <div style={searchWrap}>
            <Search size={14} style={{ color: COLORS.textTertiary, flexShrink: 0 }} />
            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t('site.modal.search')}
              aria-label={t('site.modal.search')}
              style={searchInput}
            />
          </div>

          <div style={tableWrap}>
            <table style={table}>
              <thead>
                <tr>
                  <th style={th}>{t('site.modal.columns.url')}</th>
                  <th style={{ ...th, width: '90px', textAlign: 'center' }} title={t('site.modal.excludeHint')}>{t('site.modal.columns.exclude')}</th>
                  <th style={{ ...th, width: '44px' }} />
                </tr>
              </thead>
              <tbody>
                {added.map((a) => (
                  <tr key={`new:${a.url}`}>
                    <td style={td}>
                      <span style={urlText}>{a.url}</span>{' '}
                      <span style={tag(SEMANTIC.viola)}>{t('site.modal.newRow')}</span>
                    </td>
                    <td style={tdCenter}>—</td>
                    <td style={tdCenter}>
                      <button
                        type="button"
                        aria-label={t('site.modal.remove')}
                        title={t('site.modal.remove')}
                        onClick={() => setAdded((prev) => prev.filter((x) => x.url !== a.url))}
                        style={iconBtn}
                      >
                        <X size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
                {rows.map((row) => {
                  const eff = effectiveRow(row, pending);
                  const isRemoved = removed.has(row.url);
                  const patternLocked = row.excluded_by === 'pattern';
                  return (
                    <tr key={row.url} data-testid="url-row" style={isRemoved ? { opacity: 0.55 } : undefined}>
                      <td style={td}>
                        <span style={{ ...urlText, textDecoration: isRemoved ? 'line-through' : 'none' }}>{row.url}</span>
                        <div style={subline}>
                          {row.category}
                          {row.off_apex && <> · {t('site.modal.offApex')}</>}
                          {patternLocked && <> · {t('site.modal.patternRule', { rule: row.excluded_by_rule })}</>}
                          {isRemoved && <> · <span style={{ color: SEMANTIC.errore.fg }}>{t('site.modal.removedTag')}</span></>}
                        </div>
                      </td>
                      <td style={tdCenter}>
                        {patternLocked ? (
                          <Lock size={14} aria-label={t('site.modal.patternRule', { rule: row.excluded_by_rule })} style={{ color: COLORS.textTertiary }} />
                        ) : (
                          <input
                            type="checkbox"
                            aria-label={t('site.modal.excludeAria', { url: row.url })}
                            checked={eff.excluded}
                            disabled={locked || isRemoved || excludeBlocked}
                            onChange={(e) => setFlag(row, 'excluded', e.target.checked)}
                          />
                        )}
                      </td>
                      <td style={tdCenter}>
                        {isRemoved ? (
                          <button type="button" aria-label={t('site.modal.undoRemove')} title={t('site.modal.undoRemove')} disabled={locked} onClick={() => toggleRemoved(row.url)} style={iconBtn}>
                            <RotateCw size={14} />
                          </button>
                        ) : (
                          <button type="button" aria-label={t('site.modal.remove')} title={t('site.modal.removeHint')} disabled={locked} onClick={() => toggleRemoved(row.url)} style={iconBtn}>
                            <X size={14} />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {loading && <div style={emptyRow}>{t('site.modal.loading')}</div>}
            {pageEmpty && <div style={emptyRow}>{debouncedQ ? t('site.modal.emptySearch') : t('site.modal.empty')}</div>}
          </div>
          {excludeBlocked && (
            <p style={hint(SEMANTIC.attenzione.fg)}>
              {t('site.modal.banners.exclusionsEditBlocked')} {meta.exclusions_edit_blocked}
            </p>
          )}
          <AdminPagination total={total} limit={PAGE_SIZE} offset={offset} onChange={setOffset} itemLabel={t('site.modal.itemLabel')} />

          {addOpen ? (
            <div style={addBox}>
              <input
                type="url"
                value={addUrl}
                onChange={(e) => setAddUrl(e.target.value)}
                placeholder={t('site.modal.addPlaceholder')}
                aria-label={t('site.modal.addUrl')}
                style={input}
              />
              <button type="button" onClick={submitAdd} style={primaryBtn(false)}>{t('site.modal.addConfirm')}</button>
              <button type="button" onClick={() => { setAddOpen(false); setAddError(''); }} style={ghostBtn(false)}>{t('site.modal.addCancel')}</button>
              {addError && <span role="alert" style={{ color: SEMANTIC.errore.fg, fontSize: '12px', flexBasis: '100%' }}>{addError}</span>}
            </div>
          ) : (
            <button type="button" onClick={() => setAddOpen(true)} disabled={locked} style={{ ...primaryBtn(locked), marginTop: '12px' }}>
              {t('site.modal.addUrl')}
            </button>
          )}
        </>
      )}

      <div style={footer}>
        <span style={{ fontSize: '12px', color: COLORS.textSecondary }}>
          {pendingCount > 0
            ? t('site.modal.footer', { total, count: pendingCount })
            : t('site.modal.footerNoPending', { total })}
        </span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
          <button type="button" onClick={requestClose} style={ghostBtn(false)}>
            {pendingCount > 0 ? t('site.modal.cancel') : t('site.modal.close')}
          </button>
          <button type="button" onClick={handleSave} disabled={locked || pendingCount === 0} style={primaryBtn(locked || pendingCount === 0)}>
            {saving ? t('site.modal.saving') : t('site.modal.save')}
          </button>
        </div>
      </div>

      {/* v201: the base explainer — «Rileggi tutto il Sito» only, and a pointer to Avanzate. Starts NOTHING. */}
      <AdminModal open={helpOpen === 'base'} onClose={() => setHelpOpen(null)} title={t('site.modal.help.reloadTitle')} width={520} role="alertdialog">
        <div style={helpCard}>
          {/* Without the keep / exclude sentence of `help.reloadBody`: those options live in Avanzate. */}
          <p style={helpText}>{t('site.modal.helpBase.body')}</p>
          <p style={{ ...helpText, color: COLORS.textSecondary }}>{t('site.modal.help.reloadWhen')}</p>
        </div>
        <p style={{ ...helpText, margin: '14px 0 16px' }}>{t('site.modal.helpBase.hint')}</p>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button type="button" onClick={() => setHelpOpen(null)} style={primaryBtn(false)}>{t('site.modal.help.close')}</button>
        </div>
      </AdminModal>

      {/* The comparing explainer starts NOTHING: it only describes the two triggers. */}
      <AdminModal open={helpOpen === 'compare'} onClose={() => setHelpOpen(null)} title={t('site.modal.help.title')} width={520} role="alertdialog">
        <div style={helpCard}>
          <div style={helpHeading}><RotateCw size={14} /> {t('site.modal.help.reloadTitle')}</div>
          <p style={helpText}>{t('site.modal.help.reloadBody')}</p>
          <p style={{ ...helpText, color: COLORS.textSecondary }}>{t('site.modal.help.reloadWhen')}</p>
        </div>
        <div style={helpCard}>
          <div style={helpHeading}><RefreshCw size={14} /> {t('site.modal.help.reindexTitle')}</div>
          <p style={helpText}>{t('site.modal.help.reindexBody')}</p>
          <p style={{ ...helpText, color: COLORS.textSecondary }}>{t('site.modal.help.reindexWhen')}</p>
        </div>
        <p style={{ ...helpText, fontWeight: 600, margin: '14px 0 16px' }}>{t('site.modal.help.rule')}</p>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button type="button" onClick={() => setHelpOpen(null)} style={primaryBtn(false)}>{t('site.modal.help.close')}</button>
        </div>
      </AdminModal>

      <ConfirmDialog
        open={confirmTrigger === 'reload'}
        onClose={() => setConfirmTrigger(null)}
        onConfirm={() => runTrigger('reload')}
        title={t('site.modal.reloadConfirmTitle')}
        message={t('site.modal.reloadConfirmMessage')}
        confirmLabel={t('site.modal.triggerConfirm')}
      />
      <ConfirmDialog
        open={confirmTrigger === 'reindex'}
        onClose={() => setConfirmTrigger(null)}
        onConfirm={() => runTrigger('reindex')}
        title={t('site.modal.reindexConfirmTitle')}
        message={t('site.modal.reindexConfirmMessage')}
        confirmLabel={t('site.modal.triggerConfirm')}
      />
      <ConfirmDialog
        open={!!apexReplace}
        onClose={() => setApexReplace(null)}
        onConfirm={() => runTrigger('reload', { confirmReplace: true })}
        title={t('site.modal.apexReplaceTitle')}
        message={t('site.modal.apexReplaceMessage', {
          newApex: apexReplace?.new_apex, currentApex: apexReplace?.current_apex, count: apexReplace?.url_count ?? 0,
        })}
        confirmLabel={t('site.modal.apexReplaceConfirm')}
        variant="danger"
      />
      <ConfirmDialog
        open={confirmEditSite}
        onClose={() => setConfirmEditSite(false)}
        onConfirm={startEditSite}
        title={t('site.modal.editConfirmTitle')}
        message={t('site.modal.editConfirmMessage')}
        confirmLabel={t('site.modal.editConfirmButton')}
      />
      <ConfirmDialog
        open={!!apexWarn}
        onClose={() => setApexWarn(null)}
        onConfirm={() => doSaveSite(apexWarn.site)}
        title={t('site.modal.apexWarnTitle')}
        message={t('site.modal.apexWarnMessage', { from: apexWarn?.from, to: apexWarn?.to })}
        confirmLabel={t('site.modal.apexWarnConfirm')}
        variant="danger"
      />
    </AdminModal>
  );
}

// ── styles ──────────────────────────────────────────────────────────────────
const box = { border: `1px solid ${COLORS.border}`, borderRadius: RADII.card, padding: '16px 18px', fontFamily: FONT_SANS };
const label = { fontSize: '12px', fontWeight: 600, color: COLORS.ink, marginBottom: '6px' };
const rowFlex = { display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' };
const readonlyField = {
  flex: '1 1 320px', minHeight: '38px', display: 'flex', alignItems: 'center', padding: '0 14px',
  borderRadius: RADII.controlInner, background: COLORS.field, fontSize: '13px', color: COLORS.ink, wordBreak: 'break-all',
};
const input = {
  flex: '1 1 320px', minHeight: '38px', padding: '0 12px', borderRadius: RADII.controlInner,
  border: `1px solid ${COLORS.borderStrong}`, fontSize: '13px', fontFamily: FONT_SANS, color: COLORS.ink,
};
const reviewBox = {
  marginTop: '14px', padding: '12px 14px', borderRadius: RADII.controlInner,
  background: SEMANTIC.attenzione.bg, color: SEMANTIC.attenzione.fg, fontSize: '12px', lineHeight: 1.5,
};
const searchWrap = {
  display: 'flex', alignItems: 'center', gap: '8px', margin: '16px 0 10px', padding: '0 12px',
  minHeight: '38px', borderRadius: RADII.controlInner, background: COLORS.field,
};
const searchInput = { flex: 1, border: 'none', background: 'transparent', outline: 'none', fontSize: '13px', fontFamily: FONT_SANS, color: COLORS.ink };
const tableWrap = { maxHeight: '340px', overflowY: 'auto', border: `1px solid ${COLORS.border}`, borderRadius: RADII.controlInner };
const table = { width: '100%', borderCollapse: 'collapse', fontFamily: FONT_SANS, fontSize: '13px' };
const th = {
  position: 'sticky', top: 0, background: COLORS.field, textAlign: 'left', padding: '8px 12px',
  borderBottom: `1px solid ${COLORS.border}`, color: COLORS.textSecondary, fontWeight: 600, fontSize: '11px',
  textTransform: 'uppercase', letterSpacing: '0.5px',
};
const td = { padding: '8px 12px', borderBottom: `1px solid ${COLORS.border}`, color: COLORS.ink, verticalAlign: 'middle' };
const tdCenter = { ...td, textAlign: 'center' };
const urlText = { fontWeight: 500, wordBreak: 'break-all' };
const subline = { fontSize: '11px', color: COLORS.textTertiary, marginTop: '2px' };
const emptyRow = { padding: '24px', textAlign: 'center', color: COLORS.textSecondary, fontSize: '13px', fontFamily: FONT_SANS };
const addBox = { display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginTop: '12px', fontFamily: FONT_SANS };
const footer = { display: 'flex', alignItems: 'center', gap: '12px', marginTop: '18px', paddingTop: '14px', borderTop: `1px solid ${COLORS.border}`, fontFamily: FONT_SANS };
const helpText = { margin: '0 0 6px', fontSize: '13px', color: COLORS.ink, lineHeight: 1.5, fontFamily: FONT_SANS };
const helpHeading = { display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: 700, color: COLORS.ink, marginBottom: '6px', fontFamily: FONT_SANS };
const helpCard = {
  marginTop: '10px', padding: '12px 14px', borderRadius: RADII.controlInner,
  border: `1px solid ${COLORS.border}`, background: COLORS.bg,
};

const iconBtn = {
  width: '28px', height: '28px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  borderRadius: RADII.controlInner, border: `1px solid ${COLORS.borderStrong}`, background: COLORS.surface,
  color: COLORS.ink, cursor: 'pointer',
};

function tag(c) {
  return { padding: '1px 7px', borderRadius: RADII.pill, fontSize: '10px', fontWeight: 600, background: c.bg, color: c.fg, verticalAlign: 'middle' };
}
function hint(color) {
  return { margin: '10px 0 0', fontSize: '12px', color, lineHeight: 1.5 };
}
function alertBox(color) {
  return {
    marginTop: '12px', padding: '10px 14px', borderRadius: RADII.controlInner,
    background: `${color}14`, border: `1px solid ${color}33`, color, fontSize: '13px', fontFamily: FONT_SANS, lineHeight: 1.5,
  };
}
function primaryBtn(disabled) {
  return {
    display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '9px 16px', borderRadius: RADII.controlInner, border: 'none',
    background: disabled ? COLORS.borderStrong : COLORS.accent, color: COLORS.surface,
    fontSize: '13px', fontWeight: 600, fontFamily: FONT_SANS, cursor: disabled ? 'not-allowed' : 'pointer',
  };
}
function ghostBtn(disabled) {
  return {
    display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 14px', borderRadius: RADII.controlInner,
    border: `1px solid ${COLORS.borderStrong}`, background: COLORS.surface, color: COLORS.ink,
    fontSize: '12px', fontFamily: FONT_SANS, cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.6 : 1,
  };
}
